import { HTMLAttributes } from 'react'

import Motion from '@/components/base/Motion'
import ScrollingIcon from '@/components/base/ScrollingIcon'
import Typography from '@/components/base/Typography'
import ProductParagraph from '@/components/sections/ProductParagraph'
import { useStore } from '@/contexts/store'

import { heroAnimation } from './animations'
import { cn } from '@/lib/utils'
import { WPImage, WPPageData } from '@/types'

export type HeroCollectionProps = HTMLAttributes<HTMLElement> &
  Partial<{
    featured_image: WPImage
    item: {
      featured_image: WPImage
      link: string
      title: {
        rendered: string
      }
      acf: {
        paragraph: {
          label: string
          sub_title: string
          text: string
        }
        ecommerce: {
          shop_online_link: {
            url: string
            title: string
            target: string
          }
          use_shopify_prod_id: boolean
          shopify_prod_id: string
        }
      }
    }[]
    theme: WPPageData['acf']['hero']['theme']
  }>

const HeroCollection = ({
  theme = 'light',
  className,
  item,
  ...props
}: HeroCollectionProps) => {
  const { strings } = useStore()
  return (
    <Motion animation={heroAnimation}>
      {(motionRef) => (
        <section
          ref={motionRef}
          className={cn([
            'h-svh-vh w-full',
            {
              'bg-dark-600 text-white': theme === 'dark',
              'bg-dark-100 text-dark-500': theme === 'light',
              'bg-dark-500 text-white': theme === 'onyx'
            },
            className
          ])}
          {...props}
        >
          <div className="relative z-10 flex h-full w-full items-center overflow-hidden">
            <ProductParagraph item={item} />
            <div
              className={cn([
                'absolute bottom-gutter left-gutter right-gutter z-10 lg:bottom-[6rem]',
                'flex flex-col justify-center gap-[6rem] lg:gap-0'
              ])}
            >
              <div className="flex items-end justify-between">
                <ScrollingIcon
                  className="lg:absolute lg:bottom-0 lg:left-0"
                  theme={theme}
                />
                <Typography
                  variant="label"
                  className="lg:absolute lg:bottom-0 lg:right-0"
                >
                  {strings?.bottom_right_string}
                </Typography>
              </div>
            </div>
          </div>
        </section>
      )}
    </Motion>
  )
}

export default HeroCollection
